import { useCallback, useEffect, useState } from 'react'
import { formatCents } from '@core/money'
import {
  describeCadence,
  loadRules,
  stopRule,
  type RecurringRule,
} from '@core/recurring'
import type { Space } from '@core/session'
import { Button, Card, Spinner } from '@design/components'
import { RuleForm } from './RuleForm'
import { Overlay } from '@design/Overlay'

/**
 * The list of things that come back.
 *
 * Lives in settings rather than as a tab because it is visited when something
 * changes — the rent goes up, an insurance ends — and not otherwise. The
 * calendar is where you see what these produce; this is only where you set
 * them up and stop them.
 */
export function RecurringScreen({
  spaces,
  userId,
  onClose,
}: {
  spaces: Space[]
  userId: string
  onClose: () => void
}) {
  const [rules, setRules] = useState<RecurringRule[] | null>(null)
  const [adding, setAdding] = useState<string | null>(null)

  const refresh = useCallback(() => {
    loadRules()
      .then(setRules)
      .catch(() => setRules([]))
  }, [])

  useEffect(refresh, [refresh])

  return (
    <Overlay>
      <header className="shrink-0 pad-safe-top px-3 pb-2 flex items-center justify-between border-b border-line">
        <span className="font-medium px-2">Vaste kosten</span>
        <button onClick={onClose} className="text-muted px-2 py-2">
          Klaar
        </button>
      </header>

      <div className="flex-1 scroll-area px-4 py-4 pad-safe-bottom">
        {!rules && <Spinner label="Even geduld…" />}

        {rules && rules.length === 0 && (
          <Card className="text-center py-6 mb-4">
            <p className="font-medium mb-1">Nog geen vaste kosten</p>
            <p className="text-sm text-muted">
              Huur, elektriciteit, verzekering — dingen die je niet elke maand
              wil fotograferen.
            </p>
          </Card>
        )}

        {(rules ?? []).map((rule) => (
          <RuleRow key={rule.id} rule={rule} userId={userId} onChanged={refresh} />
        ))}

        {spaces.map((space) => (
          <Button key={space.id} tone="secondary" onClick={() => setAdding(space.id)}>
            Nieuw in {space.name}
          </Button>
        ))}
      </div>

      {adding && (
        <RuleForm
          spaceId={adding}
          userId={userId}
          onDone={() => {
            setAdding(null)
            refresh()
          }}
          onCancel={() => setAdding(null)}
        />
      )}
    </Overlay>
  )
}

function RuleRow({
  rule,
  userId,
  onChanged,
}: {
  rule: RecurringRule
  userId: string
  onChanged: () => void
}) {
  const [busy, setBusy] = useState(false)

  return (
    <Card className={`mb-2 ${rule.active ? '' : 'opacity-60'}`}>
      <div className="flex items-baseline justify-between gap-2">
        <p className="font-medium truncate">{rule.description}</p>
        <span className="tabular-nums shrink-0">
          {rule.mode === 'variable' && '± '}
          {formatCents(rule.amountCents)}
        </span>
      </div>
      <p className="text-sm text-muted">
        {describeCadence(rule.rrule)}
        {rule.endsOn && ` · tot ${rule.endsOn}`}
        {rule.mode === 'variable' && ' · bedrag verschilt'}
      </p>

      {rule.active ? (
        <button
          disabled={busy}
          onClick={async () => {
            setBusy(true)
            await stopRule(rule, userId).catch(() => {})
            onChanged()
          }}
          className="text-sm text-muted underline underline-offset-2 mt-2"
        >
          Stoppen
        </button>
      ) : (
        <p className="text-xs text-muted mt-2">Gestopt</p>
      )}
    </Card>
  )
}
